/**
 * About Us Timeline — fixed milestones bar, scroll progress line, milestone tabs,
 * and coordination with the site header when scrolling mid-page (history template).
 */
class AboutUsTimelineController {
  /** Viewport top inset (px): timeline must overlap the area below this line to count as “in view”. */
  static VIEWPORT_TOP_INSET_PX = 250;
  /**
   * Progress-line tip Y as a fraction of viewport height (from top). Higher = tip farther down,
   * so cards reveal while still clearly in view instead of near the top edge.
   */
  static LINE_START_THRESHOLD_RATIO = 0.9;
  /**
   * Desktop: add `timeline-item-intro-once` after first intro (last card delay 0.9s + transition 0.5s).
   * Align with about-us-timeline.css `.dot-active:not(.timeline-item-intro-once)` transition-delay values.
   */
  static DESKTOP_INTRO_ONCE_MS = 1450;

  /**
   * @param {HTMLElement} root - `.about-us-timeline-root` element
   */
  constructor(root) {
    this.root = root;
    this.lastScrollY =
      window.pageYOffset || document.documentElement.scrollTop || 0;
    this.timelineHeaderBoostActive = false;
    this.lineStartThreshold =
      window.innerHeight * AboutUsTimelineController.LINE_START_THRESHOLD_RATIO;

    this.boundHandleScroll = this.handleScroll.bind(this);
    this.boundSyncOnRestore = this.syncOnRestore.bind(this);
    /** Keeps the tapped milestone `.active` until programmatic smooth scroll finishes (scroll handler otherwise overwrites it). */
    this.pendingMilestoneParagraph = null;
    /** Desktop center line: max scroll progress reached (line does not shrink on scroll-up). */
    this.lineProgressMax = 0;
    /** Last innerWidth when {@link lineProgressMax} was updated — reset latch on breakpoint resize. */
    this.lineProgressLatchWidth = null;
    /** @type {WeakMap<HTMLElement, number>} */
    this.introOnceTimeouts = new WeakMap();

    this.cacheDomReferences();
  }

  /**
   * Caches DOM references scoped to this section root.
   */
  cacheDomReferences() {
    this.milestonesBar = Utility.DomSelector.$('.timeline-milestones', this.root);
    this.timelineItemsEl = Utility.DomSelector.$('.timeline-items', this.root);
    this.timelineItems = Utility.DomSelector.$$('.timeline-item', this.root);
    this.milestoneParagraphs = Utility.DomSelector.$$('.timeline-milestones p', this.root);
    this.timelineTextEl = Utility.DomSelector.$('.timeline-text', this.root);
    this._followingMediaBanners = null;
  }

  /**
   * `.media-banner-with-text` sections placed after this timeline on the page (e.g. our-history).
   * @returns {HTMLElement[]}
   */
  getFollowingMediaBanners() {
    if (!this._followingMediaBanners) {
      this._followingMediaBanners = Array.from(
        document.querySelectorAll('.media-banner-with-text')
      ).filter(
        (el) => (this.root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
      );
    }
    return this._followingMediaBanners;
  }

  /**
   * Hide the fixed milestone bar while a following media banner overlaps its viewport strip.
   * @returns {boolean}
   */
  isTimelineMilestonesObscuredByMediaBanner() {
    const banners = this.getFollowingMediaBanners();
    if (!banners.length) return false;

    const barH = this.milestonesBar ? this.milestonesBar.offsetHeight : 0;
    const headerH = this.headerHeightPx();
    const stripBottom = headerH + barH + 8;

    return banners.some((banner) => {
      const r = banner.getBoundingClientRect();
      return r.top < stripBottom && r.bottom > headerH;
    });
  }

  /**
   * @param {string} value
   * @returns {number}
   */
  static parseCssPx(value) {
    const n = parseFloat(value);
    return Number.isNaN(n) ? 0 : n;
  }

  /**
   * @returns {number}
   */
  headerHeightPx() {
    return AboutUsTimelineController.parseCssPx(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
  }

  /**
   * @returns {boolean}
   */
  siteHeaderVisibleAtTop() {
    const sectionHeader = Utility.DomSelector.$('.section-header');
    if (!sectionHeader) return true;
    return !sectionHeader.classList.contains('header-hidden');
  }

  /**
   * Whether the timeline root overlaps the viewport below {@link AboutUsTimelineController.VIEWPORT_TOP_INSET_PX}
   * (equivalent to IntersectionObserver rootMargin `-250px 0px 0px 0px`).
   * @param {DOMRect} rect
   * @returns {boolean}
   */
  static isTimelineIntersectingViewportWithTopInset(rect) {
    const inset = AboutUsTimelineController.VIEWPORT_TOP_INSET_PX;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom > inset && rect.top < vh;
  }

  /**
   * @returns {boolean}
   */
  isMidTimelineScroll() {
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (y < 40) return false;
    return this.root.getBoundingClientRect().top <= this.headerHeightPx();
  }

  /**
   * Mirrors theme sticky header reveal for this page only.
   */
  revealSiteHeaderForTimeline() {
    const h = Utility.DomSelector.$('.section-header');
    if (!h) return;
    Utility.CSS.add(h, 'header-sticky', 'animate');
    Utility.CSS.remove(h, 'header-hidden');
  }

  /**
   * @param {number} scrollDelta
   */
  tryTimelineScrollUpHeaderReveal(scrollDelta) {
    if (scrollDelta >= 0) return;
    if (!this.isMidTimelineScroll()) return;
    this.revealSiteHeaderForTimeline();
  }

  /**
   * @param {number} scrollDelta
   * @param {number} y
   */
  updateMilestonesBarVisibility(scrollDelta, y) {
    if (!this.milestonesBar) return;
    const rootRect = this.root.getBoundingClientRect();
    const intersects =
      AboutUsTimelineController.isTimelineIntersectingViewportWithTopInset(rootRect);
    const obscuredByMediaBanner = this.isTimelineMilestonesObscuredByMediaBanner();
    const scrollingDown = scrollDelta > 0;
    const scrollingUp = scrollDelta < 0;

    if (intersects && !obscuredByMediaBanner) {
      Utility.CSS.add(this.milestonesBar, 'timeline-milestones--visible');
      Utility.CSS.remove(this.root, 'about-us-timeline-root--milestones-hidden');
    } else {
      Utility.CSS.remove(this.milestonesBar, 'timeline-milestones--visible');
      if (obscuredByMediaBanner) {
        Utility.CSS.add(this.root, 'about-us-timeline-root--milestones-hidden');
      } else {
        Utility.CSS.remove(this.root, 'about-us-timeline-root--milestones-hidden');
      }
    }
    if (scrollingDown && y > 20) {
      this.timelineHeaderBoostActive = false;
    }
    const barVisible = this.milestonesBar.classList.contains('timeline-milestones--visible');
    const offsetOn =
      barVisible &&
      this.siteHeaderVisibleAtTop() &&
      (this.timelineHeaderBoostActive || (scrollingUp && y > 20));
    if (offsetOn) {
      Utility.CSS.add(this.milestonesBar, 'timeline-milestones--offset-header');
    } else {
      Utility.CSS.remove(this.milestonesBar, 'timeline-milestones--offset-header');
    }
  }

  /**
   * @param {string} str
   * @returns {number|null}
   */
  static getFirstNumber(str) {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  /**
   * Year from a timeline block heading (may include HTML, e.g. h2 with a year only).
   * @param {string} raw
   * @returns {number|null}
   */
  static getHeadingYear(raw) {
    const t = (raw || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!t) return null;
    const year4 = t.match(/\b(1[0-9]{3}|20[0-9]{2})\b/);
    if (year4) return parseInt(year4[1], 10);
    return AboutUsTimelineController.getFirstNumber(t);
  }

  /**
   * Inclusive [min, max] span for a milestone label like "2000 - 1950" or "Today - 2000".
   * @param {string} milestoneText
   * @returns {{ min: number, max: number }|null}
   */
  static parseMilestoneRange(milestoneText) {
    const s = (milestoneText || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const currentYear = new Date().getFullYear();
    const parts = s.split(/\s*-\s*/);
    if (parts.length < 2) {
      const n = AboutUsTimelineController.getFirstNumber(s);
      return n !== null ? { min: n, max: n } : null;
    }
    const parsePart = (part) => {
      const t = (part || '').trim();
      if (!t) return null;
      if (/^today$/i.test(t)) return currentYear;
      const m = t.match(/(\d+)/);
      return m ? parseInt(m[1], 10) : null;
    };
    const a = parsePart(parts[0]);
    const b = parsePart(parts[parts.length - 1]);
    if (a === null || b === null) return null;
    return { min: Math.min(a, b), max: Math.max(a, b) };
  }

  /**
   * @returns {number[]}
   */
  getItemYears() {
    const out = [];
    for (const item of this.timelineItems) {
      const y = this.getItemYear(item);
      if (y !== null) out.push(y);
    }
    return out;
  }

  /**
   * "Today - 2000" vs "2000 - 1950": labels overlap at 2000, but content years
   * 2002–2003 should live in the 2000–1950 band. We split at the **smallest**
   * on-timeline year **after** the 2000 anchor (e.g. 2002). Everything **strictly
   * above** that value is the Today tab; (anchor, B] is 2000–1950.
   * @returns {number} B — split year (e.g. 2002); if no year > anchor, `anchor`.
   */
  getTodayRestSplitYear() {
    const p0 = this.milestoneParagraphs[0];
    if (!p0) return new Date().getFullYear();
    const r0 = AboutUsTimelineController.parseMilestoneRange(p0.textContent);
    const anchor = r0 ? r0.min : 2000;
    const after = this.getItemYears().filter((y) => y > anchor);
    if (after.length === 0) return anchor;
    return Math.min(...after);
  }

  /**
   * Which bar index (0 = left / newest label) a year belongs to. Uses non-overlapping
   * slots so 2002 matches "2000 - 1950" while 2005+ matches "Today - 2000".
   * @param {number} y
   * @returns {number}
   */
  getMilestoneIndexForYear(y) {
    const n = this.milestoneParagraphs.length;
    if (n <= 0) return -1;
    if (n === 1) return 0;

    const B = this.getTodayRestSplitYear();
    const r1 = AboutUsTimelineController.parseMilestoneRange(
      this.milestoneParagraphs[1]?.textContent
    );
    const r2 = AboutUsTimelineController.parseMilestoneRange(
      this.milestoneParagraphs[2]?.textContent
    );
    const r3 = AboutUsTimelineController.parseMilestoneRange(
      this.milestoneParagraphs[3]?.textContent
    );
    const low1 = r1 ? r1.min : 1950;
    const low2 = r2 ? r2.min : 1900;
    const low3 = r3 ? r3.min : 1868;
    const high3 = r3 ? r3.max : 1900;

    if (n === 2) {
      return y > B ? 0 : 1;
    }
    if (n === 3) {
      if (y > B) return 0;
      if (y > low1 && y <= B) return 1;
      return 2;
    }

    if (y > B) return 0;
    if (y > low1 && y <= B) return 1;
    if (y > low2 && y <= low1) return 2;
    if (y < low3) return 3;
    if (y >= low3 && y <= high3) return 3;
    return 3;
  }

  /**
   * @param {number} year
   * @returns {HTMLParagraphElement|null}
   */
  getMilestoneParagraphForYear(year) {
    const i = this.getMilestoneIndexForYear(year);
    if (i < 0) return null;
    return this.milestoneParagraphs[i] || null;
  }

  /**
   * When the year is not inside any tab range, pick the nearest range (edge distance).
   * @param {number} year
   * @returns {HTMLParagraphElement|null}
   */
  findClosestMilestoneByYear(year) {
    const bySlot = this.getMilestoneParagraphForYear(year);
    if (bySlot) return bySlot;
    let bestP = null;
    let bestDist = Infinity;
    for (const p of this.milestoneParagraphs) {
      const range = AboutUsTimelineController.parseMilestoneRange(p.textContent);
      if (!range) continue;
      const dist = Math.max(0, year - range.max, range.min - year);
      if (dist < bestDist) {
        bestDist = dist;
        bestP = p;
      }
    }
    return bestP;
  }

  /**
   * Parses `data-year` from liquid (`heading | strip_html | strip | replace: ' ', ''`).
   * @param {string|undefined} raw
   * @returns {number|null}
   */
  static parseDataYearValue(raw) {
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s) return null;
    const y4 = s.match(/\b(1[0-9]{3}|20[0-9]{2})\b/);
    if (y4) return parseInt(y4[1], 10);
    const compact = s.replace(/[^\d]/g, '');
    if (compact) {
      const n = parseInt(compact, 10);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    return (
      AboutUsTimelineController.getHeadingYear(s) || AboutUsTimelineController.getFirstNumber(s)
    );
  }

  /**
   * Year for a block: `data-year` on `.timeline-item` (see section liquid), else heading text.
   * @param {HTMLElement} item
   * @returns {number|null}
   */
  getItemYear(item) {
    if (!item) return null;
    const d = item.getAttribute('data-year');
    if (d != null && String(d).trim() !== '') {
      const n = AboutUsTimelineController.parseDataYearValue(d);
      if (n != null) return n;
    }
    const headingEl = Utility.DomSelector.$('.timeline-heading', item);
    if (!headingEl) return null;
    const t = headingEl.textContent || '';
    return (
      AboutUsTimelineController.getHeadingYear(t) || AboutUsTimelineController.getFirstNumber(t)
    );
  }

  /**
   * The timeline card for a milestone: **newest** year in that bar’s **slot**
   * (same rules as `getMilestoneIndexForYear` — 2002 for "2000 - 1950", 2025 for "Today").
   * @param {HTMLParagraphElement} p
   * @returns {HTMLElement|null}
   */
  findItemForMilestoneP(p) {
    const idx = this.milestoneParagraphs.indexOf(p);
    if (idx < 0) {
      const num = AboutUsTimelineController.getFirstNumber(p.textContent);
      if (num === null) return null;
      return this.findClosestItemToYear(num);
    }
    let bestItem = null;
    let bestY = -Infinity;
    for (const item of this.timelineItems) {
      const y = this.getItemYear(item);
      if (y === null) continue;
      if (this.getMilestoneIndexForYear(y) === idx && y > bestY) {
        bestY = y;
        bestItem = item;
      }
    }
    if (bestItem) return bestItem;
    const range = AboutUsTimelineController.parseMilestoneRange(p.textContent);
    if (range) {
      return this.findClosestItemToYear((range.min + range.max) / 2);
    }
    const num = AboutUsTimelineController.getFirstNumber(p.textContent);
    return num !== null ? this.findClosestItemToYear(num) : null;
  }

  /**
   * Which bar `<p>` corresponds to this card — same as click: a tab is active when
   * `findItemForMilestoneP(clickedP)` is that card. Prefer the inverse (all `p` where
   * `findItemForMilestoneP(p) === item`) so scroll and click stay aligned; slot-by-year
   * is only a fallback.
   *
   * @param {HTMLElement} item
   * @returns {HTMLParagraphElement|null}
   */
  getMilestoneParagraphForItemAsScrollTarget(item) {
    if (!item) return null;
    const matches = this.milestoneParagraphs.filter(
      (p) => this.findItemForMilestoneP(p) === item
    );
    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];
    const y = this.getItemYear(item);
    if (y == null) return matches[0];
    const byIdx = this.getMilestoneIndexForYear(y);
    if (byIdx >= 0) {
      const atIdx = this.milestoneParagraphs[byIdx];
      if (atIdx && matches.indexOf(atIdx) >= 0) return atIdx;
    }
    return matches[0];
  }

  /**
   * @param {HTMLElement} activeItem
   * @param {string} headingText
   * @returns {HTMLParagraphElement|null}
   */
  findMilestoneParagraphForItem(activeItem, headingText) {
    const pByTarget = this.getMilestoneParagraphForItemAsScrollTarget(activeItem);
    if (pByTarget) return pByTarget;

    const year =
      this.getItemYear(activeItem) ||
      AboutUsTimelineController.getHeadingYear(headingText) ||
      AboutUsTimelineController.getFirstNumber(headingText);
    if (year === null) return null;

    const bySlot = this.getMilestoneParagraphForYear(year);
    if (bySlot) return bySlot;

    return this.findClosestMilestoneByYear(year);
  }

  setMilestoneParagraphActive(/** @type {Element|null} */ p) {
    this.milestoneParagraphs.forEach((x) => x.classList.remove('active'));
    if (p) p.classList.add('active');
  }

  /**
   * Viewport Y from the top of the window where a milestone **click** lines up the
   * card — same math as `bindMilestoneClicks` (`window.scrollTo`: header + bar + 16).
   * @returns {number}
   */
  getClickAlignmentY() {
    const hh = this.headerHeightPx();
    const milestoneBarHeight = this.milestonesBar ? this.milestonesBar.offsetHeight : 0;
    return hh + milestoneBarHeight + 16;
  }

  /**
   * The `.timeline-item` whose year heading is closest in viewport to the
   * click-alignment line — the same "column" a clicked tab would scroll a card to.
   * @returns {HTMLElement|null}
   */
  getTimelineItemAtClickAlignment() {
    if (!this.timelineItems || this.timelineItems.length === 0) return null;
    const alignY = this.getClickAlignmentY();
    let best = null;
    let bestD = Infinity;
    for (const item of this.timelineItems) {
      const h = Utility.DomSelector.$('.timeline-heading', item);
      if (!h) continue;
      const d = Math.abs(h.getBoundingClientRect().top - alignY);
      if (d < bestD) {
        bestD = d;
        best = item;
      }
    }
    return best;
  }

  /**
   * Sets `.active` on a milestone tab using the same `findMilestoneParagraphForItem`
   * call as a milestone **click** (not tied to the progress-line `.dot-active`).
   *
   * @param {HTMLElement|null} item — the timeline block that should be treated like `target` on click
   */
  applyMilestoneForItemLikeClick(item) {
    if (!item) return;
    const headingM = Utility.DomSelector.$('.timeline-heading', item);
    const p = this.findMilestoneParagraphForItem(
      item,
      headingM ? headingM.textContent.trim() : ''
    );
    if (p) this.setMilestoneParagraphActive(p);
  }

  /**
   * @param {number} targetYear
   * @returns {HTMLElement|null}
   */
  findClosestItemToYear(targetYear) {
    let closest = null;
    let closestDiff = Infinity;
    for (const item of this.timelineItems) {
      const n = this.getItemYear(item);
      if (n === null) continue;
      const diff = Math.abs(n - targetYear);
      if (diff < closestDiff) {
        closestDiff = diff;
        closest = item;
      }
    }
    return closest;
  }

  /**
   * Main scroll handler: milestones, progress line, active milestone.
   * @param {{ skipMilestoneTabSync?: boolean }} [options]
   */
  handleScroll(options = {}) {
    this.lineStartThreshold =
      window.innerHeight * AboutUsTimelineController.LINE_START_THRESHOLD_RATIO;
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    const scrollDelta = y - this.lastScrollY;
    this.tryTimelineScrollUpHeaderReveal(scrollDelta);
    this.updateMilestonesBarVisibility(scrollDelta, y);
    this.lastScrollY = y;

    if (!this.timelineItemsEl) return;

    const viewportW = window.innerWidth;
    if (this.lineProgressLatchWidth !== viewportW) {
      this.lineProgressMax = 0;
      this.lineProgressLatchWidth = viewportW;
      this._followingMediaBanners = null;
    }

    // Mobile only: reveal when card top reaches the same tip Y as the progress line.
    if (window.innerWidth <= 991) {
      const threshold = this.lineStartThreshold;
      this.timelineItems.forEach((item) => {
        if (item.getBoundingClientRect().top <= threshold) {
          Utility.CSS.add(item, 'card-visible');
          Utility.CSS.add(item, 'timeline-item-revealed');
        } else if (!item.classList.contains('timeline-item-revealed')) {
          Utility.CSS.remove(item, 'card-visible');
        }
      });
    }

    let activeItem = null;
    const containerRect = this.timelineItemsEl.getBoundingClientRect();
    const containerHeight = this.timelineItemsEl.offsetHeight;
    const scrolled = Math.max(0, -containerRect.top + this.lineStartThreshold);
    const progress = Math.min(1, scrolled / containerHeight);
    const drawnPixels = progress * containerHeight;
    // Must match .timeline-items:before { height: calc(100% - Npx) } in about-us-timeline.css
    const lineBottomInsetPx = 60;
    const lineTrackPx = Math.max(1, containerHeight - lineBottomInsetPx);
    const lineTipY = progress * lineTrackPx;
    if (viewportW > 991) {
      this.lineProgressMax = Math.min(1, Math.max(this.lineProgressMax, progress));
      this.timelineItemsEl.style.setProperty(
        '--line-progress',
        String(this.lineProgressMax)
      );
    } else {
      this.timelineItemsEl.style.setProperty('--line-progress', String(progress));
    }

    this.timelineItems.forEach((item) => {
      const ir = item.getBoundingClientRect();
      const cardTop = ir.top - containerRect.top;
      const cardMidY = cardTop + ir.height * 0.5;
      const isDesktop = window.innerWidth > 991;
      const lineCrossesCardCenter = lineTipY >= cardMidY;
      const wasDotActive = item.classList.contains('dot-active');

      const yCenterViewport = ir.top + ir.height * 0.5;
      const dotTopFromPadding = yCenterViewport - (ir.top + item.clientTop);
      item.style.setProperty('--card-dot-y', `${dotTopFromPadding}px`);

      if (isDesktop) {
        if (lineCrossesCardCenter) {
          Utility.CSS.add(item, 'card-visible');
          Utility.CSS.add(item, 'timeline-item-revealed');
        } else if (!item.classList.contains('timeline-item-revealed')) {
          Utility.CSS.remove(item, 'card-visible');
        }
      } else {
        if (cardTop <= drawnPixels) {
          Utility.CSS.add(item, 'card-visible');
          Utility.CSS.add(item, 'timeline-item-revealed');
        } else if (!item.classList.contains('timeline-item-revealed')) {
          Utility.CSS.remove(item, 'card-visible');
        }
      }

      if (lineCrossesCardCenter) {
        Utility.CSS.add(item, 'timeline-item-dot');
        Utility.CSS.add(item, 'dot-active');
        activeItem = item;
        if (
          isDesktop &&
          !wasDotActive &&
          !item.classList.contains('timeline-item-intro-once')
        ) {
          const prev = this.introOnceTimeouts.get(item);
          if (prev) window.clearTimeout(prev);
          const t = window.setTimeout(() => {
            Utility.CSS.add(item, 'timeline-item-intro-once');
            this.introOnceTimeouts.delete(item);
          }, AboutUsTimelineController.DESKTOP_INTRO_ONCE_MS);
          this.introOnceTimeouts.set(item, t);
        }
      } else {
        Utility.CSS.remove(item, 'dot-active');
        if (!item.classList.contains('timeline-item-revealed')) {
          Utility.CSS.remove(item, 'timeline-item-dot');
        }
      }
    });

    if (!options.skipMilestoneTabSync) {
      if (this.pendingMilestoneParagraph) {
        this.setMilestoneParagraphActive(this.pendingMilestoneParagraph);
      } else {
        const itemLikeClick = this.getTimelineItemAtClickAlignment();
        this.applyMilestoneForItemLikeClick(itemLikeClick || activeItem);
      }
    }

    if (this.timelineTextEl) {
      const allVisible =
        this.timelineItems.length > 0 &&
        this.timelineItems.every((item) =>
          item.classList.contains('timeline-item-revealed')
        );
      Utility.CSS.toggle(this.timelineTextEl, 'timeline-text--visible', allVisible);
    }
  }

  /**
   * After load / bfcache / resize reflow, re-sync scroll baseline and optional header boost.
   */
  syncOnRestore() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.lastScrollY =
          window.pageYOffset || document.documentElement.scrollTop || 0;
        if (this.isMidTimelineScroll()) {
          this.revealSiteHeaderForTimeline();
          this.timelineHeaderBoostActive = true;
        }
        this.handleScroll();
      });
    });
  }

  /**
   * Clears click-driven milestone lock after programmatic smooth scroll settles.
   */
  clearPendingMilestoneAfterClickScroll() {
    const finalP = this.pendingMilestoneParagraph;
    this.pendingMilestoneParagraph = null;
    const isMobile = window.innerWidth <= 991;
    this.handleScroll({ skipMilestoneTabSync: isMobile });
    if (finalP && isMobile) {
      this.setMilestoneParagraphActive(finalP);
    }
  }

  /**
   * Binds milestone paragraph clicks (scroll to matching item).
   */
  bindMilestoneClicks() {
    this.milestoneParagraphs.forEach((p) => {
      /** Suppresses the synthetic `click` after `touchend` on the same tap (mobile double-fire). */
      let suppressClickFromTouch = false;
      let touchStartX = 0;
      let touchStartY = 0;
      const runMilestoneActivate = () => {
        const target = this.findItemForMilestoneP(p);
        if (!target) return;
        const headingEl = Utility.DomSelector.$('.timeline-heading', target);
        /** @type {HTMLParagraphElement|null} */
        let lockedP = null;
        if (headingEl) {
          const pActive = this.findMilestoneParagraphForItem(
            target,
            headingEl.textContent.trim()
          );
          lockedP = pActive || p;
          this.setMilestoneParagraphActive(lockedP);
        } else {
          lockedP = p;
          this.setMilestoneParagraphActive(p);
        }
        this.pendingMilestoneParagraph = lockedP;

        const hh = this.headerHeightPx();
        const milestoneBarHeight = this.milestonesBar ? this.milestonesBar.offsetHeight : 0;
        const targetTop =
          target.getBoundingClientRect().top +
          (window.pageYOffset || window.scrollY) -
          hh -
          milestoneBarHeight -
          16;

        let finished = false;
        let debounceId = /** @type {number|null} */ (null);
        /** iOS often lacks scrollend; inertia can pause briefly — use a longer settle window on narrow viewports. */
        const settleMs = window.innerWidth <= 991 ? 420 : 180;
        const finish = () => {
          if (finished) return;
          finished = true;
          window.removeEventListener('scrollend', onScrollEnd);
          window.removeEventListener('scroll', onScrollWhileSmooth);
          if (debounceId != null) window.clearTimeout(debounceId);
          window.clearTimeout(safetyId);
          this.clearPendingMilestoneAfterClickScroll();
        };
        const onScrollEnd = () => finish();
        const onScrollWhileSmooth = () => {
          if (debounceId != null) window.clearTimeout(debounceId);
          debounceId = window.setTimeout(finish, settleMs);
        };

        window.addEventListener('scrollend', onScrollEnd, { once: true });
        window.addEventListener('scroll', onScrollWhileSmooth, { passive: true });
        const safetyId = window.setTimeout(finish, 4500);

        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      };

      p.addEventListener(
        'touchstart',
        (e) => {
          const t = e.touches[0];
          if (t) {
            touchStartX = t.clientX;
            touchStartY = t.clientY;
          }
        },
        { passive: true }
      );
      p.addEventListener(
        'touchend',
        (e) => {
          const t = e.changedTouches[0];
          if (!t) return;
          const moved =
            Math.abs(t.clientX - touchStartX) > 12 || Math.abs(t.clientY - touchStartY) > 12;
          if (moved) {
            suppressClickFromTouch = true;
            return;
          }
          suppressClickFromTouch = true;
          runMilestoneActivate();
        },
        { passive: true }
      );
      p.addEventListener('click', () => {
        if (suppressClickFromTouch) {
          suppressClickFromTouch = false;
          return;
        }
        runMilestoneActivate();
      });
    });
  }

  /**
   * Registers window listeners and runs initial layout pass.
   * @returns {AboutUsTimelineController}
   */
  attach() {
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
    window.addEventListener('resize', this.boundHandleScroll, { passive: true });
    window.addEventListener('load', this.boundSyncOnRestore, { passive: true });
    window.addEventListener('pageshow', this.boundSyncOnRestore, { passive: true });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.boundSyncOnRestore, { once: true });
    } else {
      this.syncOnRestore();
    }

    this.bindMilestoneClicks();
    this.handleScroll();
    return this;
  }

  /**
   * Initializes all timeline sections on the page (typically one).
   */
  static initAll() {
    Utility.DomSelector.$$('.about-us-timeline-root[data-about-us-timeline]').forEach((root) => {
      if (root.dataset.timelineInitialized === 'true') return;
      root.dataset.timelineInitialized = 'true';
      const controller = new AboutUsTimelineController(root);
      controller.attach();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => AboutUsTimelineController.initAll());
} else {
  AboutUsTimelineController.initAll();
}
